import React from "react";
import { Maximize, Minus, Plus } from "lucide-react";
import { useMapInstance } from "./MapFrame";

interface MapToolbarProps {
  /**
   * Inline map: the header title ("Distribution areas in {city}") shown above the
   * search row inside a floating card. Omitted in the modal, where the title lives
   * in the modal chrome and only the search floats over the map.
   */
  title?: string;
  /** The search element for this channel (H2H creates a pin, D2D pans). */
  children: React.ReactNode;
  /** Shows the "Expand map" control (inline map only; hidden inside the modal). */
  expandable?: boolean;
  onExpand?: () => void;
}

// Custom +/- zoom buttons pinned bottom-right, replacing Google's default
// control so the chrome matches the Figma. Steps the shared map instance.
const ZoomControl = () => {
  const { map } = useMapInstance();
  const step = (delta: number) => {
    if (!map) return;
    const zoom = map.getZoom();
    if (typeof zoom === "number") map.setZoom(zoom + delta);
  };
  return (
    <div className="absolute bottom-4 right-4 z-10 flex flex-col overflow-hidden rounded-lg border border-solid border-border bg-background shadow-sm">
      <button
        type="button"
        aria-label="Zoom in"
        onClick={() => step(1)}
        className="flex size-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
        data-cypress-id="targeting-map-zoom-in"
      >
        <Plus className="size-4" />
      </button>
      <div className="h-px w-full bg-border" />
      <button
        type="button"
        aria-label="Zoom out"
        onClick={() => step(-1)}
        className="flex size-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
        data-cypress-id="targeting-map-zoom-out"
      >
        <Minus className="size-4" />
      </button>
    </div>
  );
};

const ExpandButton = ({ onExpand }: { onExpand?: () => void }) => (
  <button
    type="button"
    onClick={onExpand}
    className="flex h-11 shrink-0 items-center gap-2 rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent"
    data-cypress-id="targeting-map-expand"
  >
    <Maximize className="size-4" />
    Expand map
  </button>
);

// The map's top overlay (AC25). Inline: a floating white card with the city
// title above a row of [search] [Expand map]. Modal: just the search floating
// over the map (the title lives in the modal header; no nested expand control).
// Plus the custom zoom control. Rendered inside MapFrame so it reaches the map
// instance; it floats over the map without blocking the rest of it.
export const MapToolbar = ({ title, children, expandable = false, onExpand }: MapToolbarProps) => (
  <>
    {title ? (
      <div className="pointer-events-none absolute inset-x-3 top-3 z-10">
        <div className="pointer-events-auto flex flex-col gap-3 rounded-xl border border-solid border-border bg-background p-3 shadow-md">
          <p className="px-1 text-sm font-medium text-foreground">{title}</p>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">{children}</div>
            {expandable && <ExpandButton onExpand={onExpand} />}
          </div>
        </div>
      </div>
    ) : (
      <div className="pointer-events-none absolute inset-x-4 top-4 z-10">
        <div className="pointer-events-auto">{children}</div>
      </div>
    )}
    <ZoomControl />
  </>
);
